/**
 * Bridge page that mounts the redesigned <AppShell> + <CandidateVault>.
 *
 * Pulls real per-tab completion from /api/candidate/vault/completion, and
 * renders <VaultDocumentSection> as the per-tab content for documents-type
 * tabs. Structured-form tabs render placeholders until next pass.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import AppShell, { PageHeader } from './AppShell';
import CandidateVault, { BANKING_VAULT_TABS } from './CandidateVault';
import VaultDocumentSection from './VaultDocumentSection';
import { VAULT_TAB_META } from './vaultTabMeta';
import { logger } from '../utils/logger';
import AuditTrailPanel from './AuditTrailPanel';
import ShareVaultButton from './ShareVaultButton';
import VaultAccessRequestsPanel from './VaultAccessRequestsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VaultPage() {
  const { user, logout } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [completion, setCompletion] = useState({});
  const persona = user?.role === 'candidate' ? 'self' : 'recruiter';

  const loadCompletion = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/candidate/vault/completion`);
      setCompletion(data || {});
    } catch (e) {
      // Endpoint is candidate-only; recruiters & unauthenticated callers get 403/401.
      // This is expected behaviour for those personas — log at debug level only.
      logger.debug('vault completion skipped', e?.response?.status);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      try {
        const profileRes = await axios.get(`${API}/candidate/profile`).catch(() => null);
        const profile = profileRes?.data || {};
        if (!alive) return;
        setCandidate({
          full_name: user.full_name,
          email: user.email,
          phone: profile.phone || user.phone,
          dob: profile.dob,
          ni_number: profile.ni_number,
          address: profile.address,
          city: profile.city,
          target_role: profile.target_role || profile.headline,
          required_tabs: profile.required_tabs || [],
        });
      } catch (e) {
        logger.error('vault load', e);
      }
    })();
    return () => { alive = false; };
  }, [user]);

  useEffect(() => { loadCompletion(); }, [loadCompletion]);

  const handleReveal = async (label) => {
    try {
      await axios.post(`${API}/audit/reveal-field`, { field: label, target_user_id: user?.id }).catch(() => null);
    } catch (e) {
      // Reveal-field audit logging is best-effort — never block the UI on its failure.
      logger.warn('reveal-field audit failed', e?.message);
    }
  };

  const renderTab = (tab) => {
    if (!tab) return null;
    const meta = VAULT_TAB_META[tab.id] || {};
    if (meta.type === 'documents') {
      return (
        <VaultDocumentSection
          sectionId={tab.id}
          guidance={meta.guidance}
          acceptedTypes={meta.accept || '.pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx'}
          emptyState={meta.empty || 'No documents uploaded yet.'}
          onChange={loadCompletion}
        />
      );
    }
    if (meta.type === 'system') {
      return <AuditTrailPanel />;
    }
    return (
      <p className="text-sm text-[var(--vh-text-muted)] py-6 text-center" data-testid={`vault-placeholder-${tab.id}`}>
        Structured form for this section ships in the next pass.
      </p>
    );
  };

  if (!user || !candidate) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--vh-text-muted)]" data-testid="vault-loading">
        Loading your vault…
      </div>
    );
  }

  return (
    <AppShell user={user} onLogout={logout}>
      <PageHeader
        eyebrow="Compliance"
        title="My vault"
        subtitle={persona === 'recruiter'
          ? 'You are viewing this candidate as a recruiter. Sensitive fields are redacted; reveal events are audit-logged.'
          : 'Upload, review, and manage every document in your hiring compliance pack — owned by you, shared on your terms.'}
        actions={persona === 'self' ? <ShareVaultButton /> : null}
      />
      {persona === 'self' && <VaultAccessRequestsPanel />}
      <CandidateVault
        candidate={candidate}
        persona={persona}
        completion={completion}
        renderTab={renderTab}
        onRevealField={handleReveal}
      />
    </AppShell>
  );
}
